/**
 * RefineryTree: the latest scoring night's tests of one benchmark, each
 * opening to what is known about it and the proven tasks generated from it.
 *
 * A test row says how the served model has been doing on the test over
 * its latest nights, what its latest failure-analysis round judged it, with
 * a link to the record that round wrote of it, and how many tasks under it
 * the gate proves; held out and where staff's ask to generate again stands
 * are muted words beside the name. Opened, the row shows the test's path,
 * what the base policy scored on it before any training step, what the
 * reviewer made of the planned cards, and the task table. A task row says
 * how the gate's reference runs and base-policy runs went and what trained
 * on it, and opens to its case files. The checkboxes hold the selection
 * staff act on (RefineryActions): every test has one, which ticks the test
 * and every task under it.
 */

import { Fragment, useId, useRef, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'

import type { RefineryTask, RefineryTest } from '@/api/generated/model'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { SideScrollArrows } from '@/components/trajectories/SideScrollArrows'
import { cn } from '@/lib/utils'

import { FilesBand } from './FilesBand'
import { recordPath, roundLabel } from './record'
import {
  REFINERY_COPY,
  basePolicyLabel,
  filesLabel,
  nameParts,
  nightlyLabel,
  openRecordLabel,
  passesLabel,
  regenerateLabel,
  selectTaskLabel,
  selectTestLabel,
  taskKey,
  testDetail,
  testName,
  testVerdict,
  unresolvedLabel,
} from './rows'
import { type Ticked, toggleTask, toggleTest } from './tree'

/** What is ticked, and where a tick goes. */
export interface Selection {
  ticked: Ticked
  onChange: (next: Ticked) => void
}

/** A count never wraps: "3 of 3" is one line, so the row stays one line tall. */
const NUMBER_CELL = 'whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground'
/** The name column is capped so the number columns keep their room. */
const NAME_CELL = 'min-w-0 max-w-md'
/** A fact said as a word beside a name: held out, excluded, a regenerate state. */
const MUTED_WORD = 'font-mono text-[10px] uppercase tracking-wider text-muted-foreground'
/** The checkbox column of both tables: one width, so the boxes line up,
    and no padding, so the box sits in the middle of its cell. The table
    primitive strips the right padding of a checkbox cell; this cell has none. */
const CHECK_CELL = 'w-10 p-0 text-center'
const CHECK_BOX = 'inline-flex items-center justify-center align-middle'
const TEXT_BUTTON =
  'text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground'
/** A button that opens what sits under it, with a chevron that turns as it opens. */
const OPENER = 'inline-flex items-center gap-1.5 text-left'

/** A test name that wraps instead of holding its column open: the other
 * columns never wrap, so one long name would push the table past its box
 * and a scroll arrow over the last header. It breaks between its words, and
 * inside a word only when one word fills the line. */
function WrappingName({ name }: { name: string }): React.JSX.Element {
  return (
    <span className="[overflow-wrap:anywhere]">
      {nameParts(name).map((part, index) => (
        <Fragment key={index}>
          {index > 0 && <wbr />}
          {part}
        </Fragment>
      ))}
    </span>
  )
}

function Chevron({ open }: { open: boolean }): React.JSX.Element {
  return (
    <ChevronRight
      aria-hidden="true"
      className={cn('h-3 w-3 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')}
    />
  )
}

interface RecordLinkProps {
  recordId: string
  /** The round whose copy to open; a task's record names none (`recordPath`). */
  round: string | null
  ariaLabel?: string
  children: React.ReactNode
}

/** A link to a failure record's page, on the benchmark the refinery shows. */
function RecordLink({ recordId, round, ariaLabel, children }: RecordLinkProps): React.JSX.Element {
  const [searchParams] = useSearchParams()
  return (
    <Link
      to={recordPath(recordId, searchParams.get('benchmark'), round)}
      title={recordId}
      aria-label={ariaLabel}
      className="underline underline-offset-2 hover:text-muted-foreground"
    >
      {children}
    </Link>
  )
}

/** The test's latest verdict with its round's day, which can be older than the
 * night beside it, opening the record that round wrote of the test. */
function VerdictCell({ test }: { test: RefineryTest }): React.JSX.Element {
  const analysis = test.failure_analysis
  const verdict = testVerdict(test)
  const word = REFINERY_COPY.verdicts[verdict]
  const quiet = verdict === 'passing' || verdict === 'noRound'
  return (
    <TableCell className={cn('whitespace-nowrap', quiet && 'text-muted-foreground')}>
      {analysis !== null && analysis.record_id !== null ? (
        <RecordLink recordId={analysis.record_id} round={analysis.round} ariaLabel={openRecordLabel(test)}>
          {word}
        </RecordLink>
      ) : (
        word
      )}
      {analysis !== null && (
        <span className="ml-2 font-mono text-xs text-muted-foreground">{roundLabel(analysis.round)}</span>
      )}
    </TableCell>
  )
}

function TaskRow({ task, selection }: { task: RefineryTask; selection: Selection }): React.JSX.Element {
  const [filesOpen, setFilesOpen] = useState(false)
  const bandId = useId()
  return (
    <>
      <TableRow>
        <TableCell className={CHECK_CELL}>
          <span className={CHECK_BOX}>
            <Checkbox
              checked={selection.ticked.tasks.has(taskKey(task))}
              aria-label={selectTaskLabel(task)}
              onCheckedChange={() => {
                selection.onChange(toggleTask(selection.ticked, task))
              }}
            />
          </span>
        </TableCell>
        <TableCell className={NAME_CELL}>
          <div className="flex flex-wrap items-center gap-2">
            {task.source === 'failure-record' ? (
              <RecordLink recordId={task.record_id} round={null}>
                {task.case}
              </RecordLink>
            ) : (
              <span title={task.record_id}>{task.case}</span>
            )}
            {task.excluded && <span className={MUTED_WORD}>{REFINERY_COPY.excluded}</span>}
            <button
              type="button"
              onClick={() => {
                setFilesOpen((current) => !current)
              }}
              aria-expanded={filesOpen}
              aria-controls={bandId}
              aria-label={filesLabel(task)}
              className={TEXT_BUTTON}
            >
              {REFINERY_COPY.files}
            </button>
          </div>
        </TableCell>
        <TableCell className={NUMBER_CELL}>{passesLabel(task)}</TableCell>
        <TableCell className={NUMBER_CELL} title={task.base_policy?.model}>
          {basePolicyLabel(task)}
        </TableCell>
        <TableCell className="hidden font-mono text-xs text-muted-foreground lg:table-cell">
          {/* The em dash is the table's null placeholder (ModelsTable.tsx). */}
          {task.datasets.length > 0 ? task.datasets.join(', ') : '—'}
        </TableCell>
      </TableRow>
      {filesOpen && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={5} className="p-0">
            <FilesBand task={task} id={bandId} />
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

/** The proven tasks under one test, or the ones under none. */
export function TaskTable({ tasks, selection }: { tasks: RefineryTask[]; selection: Selection }): React.JSX.Element {
  const columns = REFINERY_COPY.taskColumns
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className={CHECK_CELL} />
          <TableHead>{columns.task}</TableHead>
          <TableHead>{columns.passes}</TableHead>
          <TableHead>{columns.basePolicy}</TableHead>
          <TableHead className="hidden lg:table-cell">{columns.datasets}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {tasks.map((task) => (
          <TaskRow key={taskKey(task)} task={task} selection={selection} />
        ))}
      </TableBody>
    </Table>
  )
}

/** The opened row: the test's path, its step 0 and reviewer sentences, then its tasks. Exported for its test. */
export function TestDetails({ test, id, selection }: { test: RefineryTest; id: string; selection: Selection }): React.JSX.Element {
  return (
    <div id={id} className="space-y-2 border-l-2 border-border bg-muted/30 py-3 pl-4">
      <p className="break-all font-mono text-[10px] text-muted-foreground">{test.module}</p>
      <p className="text-sm text-muted-foreground">{testDetail(test)}</p>
      {test.tasks.length > 0 && <TaskTable tasks={test.tasks} selection={selection} />}
    </div>
  )
}

function TestRow({ test, selection }: { test: RefineryTest; selection: Selection }): React.JSX.Element {
  const [open, setOpen] = useState(false)
  const detailsId = useId()
  return (
    <>
      <TableRow>
        <TableCell className={CHECK_CELL}>
          <span className={CHECK_BOX}>
            <Checkbox
              checked={selection.ticked.tests.has(test.test_id)}
              aria-label={selectTestLabel(test)}
              onCheckedChange={() => {
                selection.onChange(toggleTest(selection.ticked, test))
              }}
            />
          </span>
        </TableCell>
        <TableCell className={NAME_CELL}>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <button
              type="button"
              onClick={() => {
                setOpen((current) => !current)
              }}
              aria-expanded={open}
              aria-controls={detailsId}
              aria-label={testName(test)}
              title={test.module}
              className={cn(OPENER, 'font-medium')}
            >
              <Chevron open={open} />
              <WrappingName name={test.family} />
            </button>
            {test.held_out && <span className={MUTED_WORD}>{REFINERY_COPY.heldOut}</span>}
            {test.regenerate !== null && <span className={MUTED_WORD}>{regenerateLabel(test.regenerate)}</span>}
          </div>
        </TableCell>
        <TableCell className={NUMBER_CELL}>{nightlyLabel(test.nightly)}</TableCell>
        <VerdictCell test={test} />
        {/* Every task the gate proves under the test, failure records' and
            recovered batches' too, so it is no fraction of the cards the
            test's own starts planned (the reviewer sentence says those). */}
        <TableCell className={cn(NUMBER_CELL, 'text-right')}>{test.generation.proven}</TableCell>
      </TableRow>
      {open && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={5} className="p-0">
            <TestDetails test={test} id={detailsId} selection={selection} />
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

export function RefineryTree({ tests, selection }: { tests: RefineryTest[]; selection: Selection }): React.JSX.Element {
  const columns = REFINERY_COPY.testColumns
  const scrollRef = useRef<HTMLDivElement>(null)
  return (
    // `relative` anchors the floating scroll arrows to this box (JobMapTable).
    <div className="relative">
      <SideScrollArrows scrollRef={scrollRef} />
      <Table containerRef={scrollRef}>
        <TableHeader>
          <TableRow>
            <TableHead className={CHECK_CELL} />
            <TableHead>{columns.test}</TableHead>
            <TableHead>{columns.nightly}</TableHead>
            <TableHead>{columns.verdict}</TableHead>
            <TableHead className="text-right">{columns.proven}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tests.map((test) => (
            <TestRow key={test.test_id} test={test} selection={selection} />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

/** The tasks under no test of the night, folded into one muted line that opens their table. */
export function UnresolvedTasks({ tasks, selection }: { tasks: RefineryTask[]; selection: Selection }): React.JSX.Element {
  const [open, setOpen] = useState(false)
  const tableId = useId()
  return (
    <section className="space-y-2">
      <button
        type="button"
        onClick={() => {
          setOpen((current) => !current)
        }}
        aria-expanded={open}
        aria-controls={tableId}
        className={cn(OPENER, 'text-sm text-muted-foreground hover:text-foreground')}
      >
        <Chevron open={open} />
        {unresolvedLabel(tasks.length)}
      </button>
      {open && (
        <div id={tableId}>
          <TaskTable tasks={tasks} selection={selection} />
        </div>
      )}
    </section>
  )
}
